import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, ChevronDown, X } from "lucide-react";
import { api } from "@/lib/api";
import { FUEL_BG, TRANS_BG, BODY_BG } from "@/lib/site";
import { useNavigate } from "react-router-dom";

const FUELS = ["Petrol", "Diesel", "Hybrid", "Electric"];
const TRANS = ["Automatic", "Manual"];
const BODIES = ["Sedan", "Coupe", "SUV", "Convertible"];
const CATEGORIES = ["car", "jeep", "van"];
const CATEGORY_BG = {
    car: "Автомобили",
    jeep: "Джипове",
    van: "Бусове",
};

function Dropdown({ label, value, options, onChange, getLabel, dataTestId }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="relative">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center justify-between gap-3 px-5 py-4 bg-surface2 border border-line rounded-xl text-left hover:border-gold/40 transition-all"
                data-testid={dataTestId}
            >
                <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-ink-subtle">
                        {label}
                    </div>
                    <div className="text-sm mt-1 text-ink/90">
                        {value ? (getLabel ? getLabel(value) : value) : "Всички"}
                    </div>
                </div>
                <ChevronDown
                    size={16}
                    className={`text-gold/70 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
                />
            </button>
            <AnimatePresence>
                {open && (
                    <motion.ul
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.18 }}
                        className="absolute z-30 mt-2 w-full glass rounded-xl overflow-hidden max-h-64 overflow-y-auto"
                    >
                        <li>
                            <button
                                type="button"
                                onClick={() => {
                                    onChange("");
                                    setOpen(false);
                                }}
                                className="w-full text-left px-5 py-3 text-sm text-ink-muted hover:bg-gold/10 hover:text-gold"
                            >
                                Всички
                            </button>
                        </li>
                        {options.map((opt) => (
                            <li key={opt}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        onChange(opt);
                                        setOpen(false);
                                    }}
                                    className="w-full text-left px-5 py-3 text-sm hover:bg-gold/10 hover:text-gold"
                                >
                                    {getLabel ? getLabel(opt) : opt}
                                </button>
                            </li>
                        ))}
                    </motion.ul>
                )}
            </AnimatePresence>
        </div>
    );
}

export default function SearchPanel({ embedded = false, initialFilters }) {
    const navigate = useNavigate();
    const [brands, setBrands] = useState([]);
    const [filters, setFilters] = useState({
        category: "",
        brand: "",
        fuel: "",
        transmission: "",
        body_style: "",
        year_min: "",
        year_max: "",
        price_min: "",
        price_max: "",
        mileage_max: "",
        hp_min: "",
        ...(initialFilters || {}),
    });

    useEffect(() => {
        api.get("/cars/meta/brands").then((r) => setBrands(r.data));
    }, []);

    const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

    const handleSearch = () => {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([k, v]) => {
            if (v !== "" && v !== null && v !== undefined) params.set(k, v);
        });
        navigate(`/cars?${params.toString()}`);
    };

    const reset = () =>
        setFilters({
            category: "",
            brand: "",
            fuel: "",
            transmission: "",
            body_style: "",
            year_min: "",
            year_max: "",
            price_min: "",
            price_max: "",
            mileage_max: "",
            hp_min: "",
        });

    return (
        <section
            id="search"
            className={`relative ${embedded ? "py-0" : "py-20 lg:py-28"}`}
            data-testid="search-section"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                {!embedded && (
                    <div className="text-center mb-14">
                        <span className="block text-gold text-[11px] uppercase tracking-[0.32em] mb-5">
                            Намери своя автомобил
                        </span>
                        <h2 className="font-display text-4xl sm:text-5xl tracking-tightest">
                            Разширено търсене
                        </h2>
                    </div>
                )}

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className="glass rounded-3xl p-7 lg:p-10"
                >
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <Dropdown
                            label="Категория"
                            value={filters.category}
                            options={CATEGORIES}
                            getLabel={(v) => CATEGORY_BG[v] || v}
                            onChange={(v) => set("category", v)}
                            dataTestId="filter-category"
                        />
                        <Dropdown
                            label="Марка"
                            value={filters.brand}
                            options={brands}
                            onChange={(v) => set("brand", v)}
                            dataTestId="filter-brand"
                        />
                        <Dropdown
                            label="Тип каросерия"
                            value={filters.body_style}
                            options={BODIES}
                            getLabel={(v) => BODY_BG[v] || v}
                            onChange={(v) => set("body_style", v)}
                            dataTestId="filter-body"
                        />
                        <Dropdown
                            label="Гориво"
                            value={filters.fuel}
                            options={FUELS}
                            getLabel={(v) => FUEL_BG[v] || v}
                            onChange={(v) => set("fuel", v)}
                            dataTestId="filter-fuel"
                        />
                        <Dropdown
                            label="Скоростна кутия"
                            value={filters.transmission}
                            options={TRANS}
                            getLabel={(v) => TRANS_BG[v] || v}
                            onChange={(v) => set("transmission", v)}
                            dataTestId="filter-transmission"
                        />
                        <div className="grid grid-cols-2 gap-2">
                            <NumInput
                                label="Год. от"
                                value={filters.year_min}
                                onChange={(v) => set("year_min", v)}
                                testid="filter-year-min"
                            />
                            <NumInput
                                label="Год. до"
                                value={filters.year_max}
                                onChange={(v) => set("year_max", v)}
                                testid="filter-year-max"
                            />
                        </div>
                        <NumInput
                            label="Макс. пробег (км)"
                            value={filters.mileage_max}
                            onChange={(v) => set("mileage_max", v)}
                            testid="filter-mileage-max"
                        />
                        <div className="grid grid-cols-2 gap-2">
                            <NumInput
                                label="Цена от (EUR)"
                                value={filters.price_min}
                                onChange={(v) => set("price_min", v)}
                                testid="filter-price-min"
                            />
                            <NumInput
                                label="Цена до (EUR)"
                                value={filters.price_max}
                                onChange={(v) => set("price_max", v)}
                                testid="filter-price-max"
                            />
                        </div>
                        <NumInput
                            label="Мин. к.с."
                            value={filters.hp_min}
                            onChange={(v) => set("hp_min", v)}
                            testid="filter-hp-min"
                        />
                    </div>

                    <div className="mt-7 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                        <button
                            type="button"
                            onClick={reset}
                            className="inline-flex items-center gap-2 text-sm text-ink-muted hover:text-gold"
                            data-testid="filter-reset"
                        >
                            <X size={14} /> Изчисти филтрите
                        </button>
                        <button
                            type="button"
                            onClick={handleSearch}
                            className="inline-flex items-center justify-center gap-3 bg-gold hover:bg-gold-light text-bg font-medium px-8 py-4 rounded-full transition-all"
                            data-testid="filter-search-btn"
                        >
                            <Search size={16} /> Търси автомобили
                        </button>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}

function NumInput({ label, value, onChange, testid }) {
    return (
        <label className="block bg-surface2 border border-line rounded-xl px-5 py-4 hover:border-gold/40 transition-all">
            <span className="block text-[10px] uppercase tracking-[0.25em] text-ink-subtle">
                {label}
            </span>
            <input
                type="number"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder="—"
                className="w-full mt-1 bg-transparent text-sm focus:outline-none placeholder:text-ink-subtle"
                data-testid={testid}
            />
        </label>
    );
}
